import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import MainLayout from "@/components/layouts";
import { ChevronRight, Save, AlertCircle } from "lucide-react";
import { PageHeader } from "@/components/layouts/page-header/index.tsx";
import { useCustomPricing } from "./custom-pricing.hooks";
import { Service } from "@/models/services.model";
import { Fragment, useState, useEffect } from "react";
import { useParams } from "react-router";
import { uuid } from "@/interfaces";

export default function CustomPricing() {
  const { propertyId } = useParams<{ propertyId: uuid }>();

  const {
    list,
    property,
    propertyNotFound,
    handleToggleService,
    openedServices,
    formData,
    handleFormChange,
    handleSave,
    loading,
    hasUnsavedChanges,
    pricesLoaded,
    pricesFailed,
  } = useCustomPricing(propertyId);

  if (propertyNotFound)
    return (
      <MainLayout breadcrumb={[{ name: "Custom Pricing", href: "#" }]}>
        <p className="text-destructive">Property not found</p>
      </MainLayout>
    );

  const breadcrumb = [
    { name: "Custom Pricing", href: "#" },
    { name: property?.property?.name || "Loading...", href: "#" },
  ];

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <PageHeader
        title={`Custom Pricing of ${property?.property?.name || "Loading..."}`}
        subtitle="Manage custom pricing for services"
        buttonText={hasUnsavedChanges ? "Save Changes" : "Save"}
        onButtonClick={handleSave}
        buttonDisabled={!pricesLoaded || loading}
      />

      {pricesFailed && (
        <div role="alert" className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-800">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">This property's prices could not be loaded. Reload the page before saving.</span>
        </div>
      )}

      {hasUnsavedChanges && (
        <div className="mx-6 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2 text-amber-800">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">You have unsaved changes</span>
        </div>
      )}
      <div className="container mx-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service</TableHead>
              <TableHead>Rate of Service</TableHead>
              <TableHead>Individual Payment</TableHead>
              <TableHead>Default</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((service) => (
              <Fragment key={service.id}>
                <TableRow>
                  <TableCell colSpan={4} className="bg-muted font-medium">
                    <div
                      className="flex items-center gap-2 cursor-pointer py-2"
                      onClick={() => handleToggleService(service.id)}
                    >
                      <ChevronRight
                        className={`h-4 w-4 transition-transform duration-300 ${
                          openedServices.includes(service.id) ? "rotate-90" : ""
                        }`}
                      />
                      {service.name}
                    </div>
                  </TableCell>
                </TableRow>

                {openedServices.includes(service.id) && (
                  <>
                    {service.children.map((sub: Service, i: number) => {
                      const formEntry = formData[sub.id];
                      const isDefault = formEntry?.isDefault ?? true;
                      const currentPrice = formEntry?.price ?? sub.price;
                      const currentPayment = formEntry?.payment ?? sub.payment;

                      return (
                        <TableRow key={i}>
                          <TableCell>{sub.name}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={currentPrice}
                              className="w-20"
                              disabled={isDefault}
                              onChange={(e) => handleFormChange(sub.id, "price", parseFloat(e.target.value) || 0)}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={currentPayment}
                              className="w-20"
                              disabled={isDefault}
                              onChange={(e) => handleFormChange(sub.id, "payment", parseFloat(e.target.value) || 0)}
                            />
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={isDefault}
                              onCheckedChange={(checked) => handleFormChange(sub.id, "isDefault", checked as boolean)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </MainLayout>
  );
}
